import { create } from 'zustand';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';

export type OrganizerAccessWindow = {
  startAt: number | null;
  endAt: number | null;
  updatedAt?: number;
  updatedBy?: string;
};

type SettingsState = {
  masterPassword: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  fetchMasterPassword: () => Promise<string>;
  updateMasterPassword: (newPassword: string) => Promise<void>;
  organizerAccessWindow: OrganizerAccessWindow | null;
  organizerAccessLoading: boolean;
  fetchOrganizerAccessWindow: () => Promise<OrganizerAccessWindow>;
  updateOrganizerAccessWindow: (startAt: number | null, endAt: number | null) => Promise<void>;
};

// Default master password if none is set in Firestore
const DEFAULT_MASTER_PASSWORD = 'admin';

export const useSettingsStore = create<SettingsState>((set, get) => ({
  masterPassword: null,
  loading: false,
  error: null,
  initialized: false,
  organizerAccessWindow: null,
  organizerAccessLoading: false,

  fetchMasterPassword: async () => {
    set({ loading: true, error: null });
    try {
      // Get the master password document
      const passwordDocRef = doc(firestore, 'MasterPassword', 'password');
      const passwordDoc = await getDoc(passwordDocRef);
      
      let password: string;
      
      // If document exists, use the password from it
      if (passwordDoc.exists()) {
        password = passwordDoc.data().password || DEFAULT_MASTER_PASSWORD;
      } else {
        // If document doesn't exist, create it with the default password
        password = DEFAULT_MASTER_PASSWORD;
        await setDoc(passwordDocRef, { password: DEFAULT_MASTER_PASSWORD });
      }
      
      set({ masterPassword: password, loading: false, initialized: true });
      return password;
    } catch (error: any) {
      console.error('Error fetching master password:', error);
      set({ error: error.message, loading: false });
      // Return default password as fallback
      return DEFAULT_MASTER_PASSWORD;
    }
  },

  updateMasterPassword: async (newPassword: string) => {
    if (!newPassword.trim()) {
      throw new Error('Password cannot be empty');
    }
    
    set({ loading: true, error: null });
    try {
      // Update the master password document
      const passwordDocRef = doc(firestore, 'MasterPassword', 'password');
      await setDoc(passwordDocRef, { 
        password: newPassword,
        updatedAt: Date.now(),
        updatedBy: localStorage.getItem('userId') || 'unknown'
      });
      
      set({ masterPassword: newPassword, loading: false });
    } catch (error: any) {
      console.error('Error updating master password:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchOrganizerAccessWindow: async () => {
    set({ organizerAccessLoading: true, error: null });
    try {
      const accessDocRef = doc(firestore, 'Settings', 'organizerAccess');
      const accessDoc = await getDoc(accessDocRef);

      let windowData: OrganizerAccessWindow = {
        startAt: null,
        endAt: null
      };

      if (accessDoc.exists()) {
        const data = accessDoc.data();
        windowData = {
          startAt: typeof data.startAt === 'number' ? data.startAt : null,
          endAt: typeof data.endAt === 'number' ? data.endAt : null,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy
        };
      } else {
        await setDoc(accessDocRef, windowData);
      }

      set({ organizerAccessWindow: windowData, organizerAccessLoading: false });
      return windowData;
    } catch (error: any) {
      console.error('Error fetching organizer access window:', error);
      set({ error: error.message, organizerAccessLoading: false });
      return {
        startAt: null,
        endAt: null
      };
    }
  },

  updateOrganizerAccessWindow: async (startAt: number | null, endAt: number | null) => {
    if (startAt && endAt && startAt > endAt) {
      throw new Error('Start time must be before end time');
    }

    set({ organizerAccessLoading: true, error: null });
    try {
      const accessDocRef = doc(firestore, 'Settings', 'organizerAccess');
      await setDoc(
        accessDocRef,
        {
          startAt,
          endAt,
          updatedAt: Date.now(),
          updatedBy: localStorage.getItem('userId') || 'unknown'
        },
        { merge: true }
      );

      set({
        organizerAccessWindow: {
          startAt,
          endAt,
          updatedAt: Date.now(),
          updatedBy: localStorage.getItem('userId') || 'unknown'
        },
        organizerAccessLoading: false
      });
    } catch (error: any) {
      console.error('Error updating organizer access window:', error);
      set({ error: error.message, organizerAccessLoading: false });
      throw error;
    }
  }
}));
